// Durable first-build attempt record — client types, pure recovery logic, and
// the thin API client. Backs interrupted-first-build recovery (migration 141)
// and, since migration 186, the PREPARED-build lifecycle: the record is now
// written BEFORE the credit gate, so a first build stopped by a paywall keeps
// its setup and resumes itself once the entitlement lands.
//
// The recovery decision is a PURE function (classifyBuildRecovery) so it can be
// unit-tested without the page's async machinery, and shared by the load path
// and the preview. Everything server-durable lives in the build_attempts table;
// this module never touches localStorage (that can't survive a browser restart
// or a cross-device reopen — the exact failure this feature fixes).

import type { Attachment, ProjectPlan } from "@/lib/types";
import type {
  SourceAdaptation,
  SourceUse,
} from "@/lib/ai/conversation-orchestrator";

/**
 * Last SAFE stage the first build reached.
 *
 * "prepared" sits BEFORE "planning" and is the stage this record now starts in:
 * setup is complete and the whole build intent is saved, but no chargeable work
 * has been asked for yet. It exists because the old first stage — "planning" —
 * was only ever written after the credit gate, so a build stopped BY that gate
 * left no trace at all (see migration 186).
 */
export type BuildStage =
  | "prepared"
  | "planning"
  | "generating"
  | "applying"
  | "preview";
/**
 * Lifecycle of a first-build attempt.
 *
 * "awaiting_entitlement" is a prepared build parked on a BILLING fact, and it is
 * deliberately not "interrupted": nothing failed and nothing was abandoned. The
 * distinction is the whole resume contract — an interrupted build offers a
 * manual "Continue building" that re-plans from the stored prompt, whereas this
 * one resumes ITSELF, from its own saved setup, the moment the entitlement lands.
 */
export type BuildStatus =
  | "active"
  | "awaiting_entitlement"
  | "interrupted"
  | "failed"
  | "completed";

/**
 * This turn's resolved source decision, as persisted.
 *
 * Structurally identical to `TurnSourceDecision` (lib/clone/source-use-client.ts)
 * and deliberately re-declared rather than imported: that module is a "use client"
 * transport and this one is read by an API route. Same shape, so the two assign
 * to each other freely.
 */
export interface PreparedTurnSource {
  sourceUse: SourceUse | null;
  urls: string[];
  /**
   * What the user wanted built from that page — `SourceAdaptation`. Optional
   * because rows written before it existed do not carry one, and a resume that
   * finds none is a resume of a build that had none.
   */
  adaptation?: SourceAdaptation | null;
  source: string;
}

/**
 * The rest of the prepared build intent — everything the resume needs that has
 * no column of its own (migration 186's `request` jsonb).
 *
 * Every field here answers the same question: what did Panda already work out
 * about this turn, so that resuming it never has to work it out AGAIN? That is
 * the ticket's "do not reinterpret the request from the first message alone" in
 * storage form. `turnSource` in particular is the orchestrator's own answer for
 * this turn; replaying it is what keeps the single semantic owner single — the
 * resume consumes a decision, it does not make one.
 */
export interface PreparedBuildRequest {
  /** The user's own turns for this build, with nothing of Panda's in them. */
  userTurns?: string[];
  /** The acknowledgement already shown before the build ("You want a …"). */
  confirmBack?: string | null;
  /** The orchestrator's source decision for this turn. */
  turnSource?: PreparedTurnSource | null;
  /** Entry point this build came from (url / chat / template / clone / growth). */
  source?: string | null;
  /** The user's message for this build is already in the thread. */
  echoed?: boolean;
  /** Vision-capable attachments this build was set up with. */
  visionImageCount?: number;
}

/**
 * What this attempt has actually cost, read from the ledger rather than assumed.
 *
 * The recovery card used to say "no credits were spent" on every interrupted
 * first build. That is only true when nothing was ever charged against the
 * attempt: generation runs detached on the server, so a tab that closed mid
 * build can leave a settled charge behind a project with no files. The read is
 * keyed on the same `buildAttemptId` the generate route charges under.
 *
 *   • "none"    — nothing reserved or kept against this attempt. Saying no
 *                 credits were spent is true.
 *   • "charged" — a charge is held or settled against it. That is the exact rule
 *                 the generate route uses to skip a second reservation, so a
 *                 resume of this attempt is not charged again.
 *   • "unknown" — the ledger could not be read for this viewer. Say nothing
 *                 about credits either way.
 */
export interface AttemptSpend {
  state: "none" | "charged" | "unknown";
}

export interface BuildAttempt {
  id: string;
  projectId: string;
  prompt: string | null;
  plan: ProjectPlan | null;
  attachments: Attachment[];
  ownershipContext: Record<string, unknown> | null;
  templateId: string | null;
  /** The prepared intent with no column of its own. Null on rows written before
   *  migration 186, and on a DB where that migration has not been applied yet. */
  request: PreparedBuildRequest | null;
  stage: BuildStage;
  status: BuildStatus;
  startedAt: string;
  updatedAt: string;
  /** The ledger's answer for this attempt. Only the GET read carries it; a
   *  write or a claim returns the bare row, which reads as unknown. */
  spend?: AttemptSpend | null;
}

/**
 * Is this a plan a build can actually run from?
 *
 * Not every non-null `projects.plan` is one. The home page creates the project
 * row before it navigates and stores `{ templateId }` in the plan column, so a
 * first build that never reached the planner leaves that stub behind. Treating
 * it as a saved plan made "Continue building" skip planning and generate from a
 * plan with no title and no features, and made the card promise a "saved plan"
 * that did not exist.
 */
export function isBuildPlan(plan: unknown): plan is ProjectPlan {
  if (!plan || typeof plan !== "object") return false;
  const p = plan as Partial<ProjectPlan>;
  return (
    typeof p.title === "string" &&
    p.title.trim().length > 0 &&
    Array.isArray(p.features)
  );
}

/**
 * How often the building client bumps the attempt heartbeat (updated_at).
 * Kept well under STALE_MS so a genuinely-live build never looks interrupted.
 */
export const BUILD_HEARTBEAT_MS = 10_000;

/**
 * How long after the last heartbeat an `active` attempt is treated as
 * interrupted. ai-generate runs detached and file application is client-owned,
 * so once the building tab is gone nothing advances the attempt — after this
 * window a returning session should offer "Continue building" rather than wait
 * on a build that will never land. ~4 missed heartbeats.
 */
export const BUILD_STALE_MS = 45_000;

/**
 * What the returning session should do about a first-build attempt.
 *   - "none"        → nothing to recover (already built, or genuinely brand-new).
 *   - "active"      → another session is actively building (fresh heartbeat);
 *                     don't start a second build — show progress / poll.
 *   - "awaiting_entitlement"
 *                   → setup is COMPLETE and saved, and the build is parked on a
 *                     billing fact (a paywall / credit gate sent the user to
 *                     checkout). Nothing failed. The session resumes this build
 *                     itself once the entitlement lands — it must never be shown
 *                     as interrupted, and must never ask the user to re-answer.
 *   - "interrupted" → the first build never landed and isn't running; offer
 *                     "Continue building" (resume from the persisted plan).
 *   - "failed"      → the build genuinely failed; use the failure recovery UI,
 *                     NOT the interrupted CTA.
 */
export type BuildRecoveryState =
  | "none"
  | "active"
  | "awaiting_entitlement"
  | "interrupted"
  | "failed";

/**
 * Decide the recovery state for a freshly-loaded project. Pure.
 *
 * The signal is "a first build was attempted but no executable app landed".
 * Three independent pieces of evidence establish that a first build was tried:
 *   • a durable attempt row (beginBuildAttempt persists one BEFORE planning, so
 *     a build that died anywhere — even before a plan existed — leaves a trace),
 *   • a persisted plan with no files (legacy plan-only projects predate the
 *     attempt table, or the attempt write was lost), or
 *   • the project's stored first-build prompt with no files (`hasPrompt`): the
 *     first build was abandoned during the pre-build phase (intent gate /
 *     ownership) BEFORE any attempt row or plan was written, so the prompt on
 *     the project row is the only remaining trace.
 * A completed attempt (or any files) means the build finished — never treated as
 * interrupted. An `active` attempt whose heartbeat is still fresh means another
 * session is mid-build. Everything else is a stranded first build that should
 * offer to continue.
 *
 * The attempt row is inspected BEFORE the no-plan short-circuit, and a stored
 * prompt counts as evidence too, so a first prompt that ran out of credits (or
 * was otherwise abandoned) at any point — during planning OR earlier, in the
 * pre-build intent/ownership step — surfaces the empty-build state rather than
 * falling back to the generic idle screen (PANDA-1560).
 */
export function classifyBuildRecovery(args: {
  hasPlan: boolean;
  /** The project row carries a non-empty first-build prompt. Evidence a build
   *  was requested even when no attempt row or plan was ever persisted. */
  hasPrompt?: boolean;
  fileCount: number;
  attempt: Pick<BuildAttempt, "status" | "updatedAt"> | null;
  nowMs: number;
}): BuildRecoveryState {
  const { hasPlan, hasPrompt = false, fileCount, attempt, nowMs } = args;
  // The app exists → nothing to recover.
  if (fileCount > 0) return "none";

  // An attempt row is proof a first build was tried — classify off its status
  // regardless of whether a plan ever landed. This catches builds that failed
  // before planning (e.g. credits ran out at the very start): the plan is null
  // but the stranded first build still needs the empty-build state.
  if (attempt) {
    if (attempt.status === "completed") return "none";
    // Parked on a billing fact. Checked BEFORE the staleness rule on purpose:
    // this row has no heartbeat by design (nothing is running — the user is at
    // a checkout, or has closed the tab entirely), so every staleness test would
    // call it interrupted and hand back the manual re-plan CTA that loses the
    // saved setup. Time does not turn a waiting build into a failed one.
    if (attempt.status === "awaiting_entitlement") return "awaiting_entitlement";
    if (attempt.status === "failed") return "failed";
    if (attempt.status === "active" && !isAttemptStale(attempt, nowMs)) {
      return "active";
    }
    // interrupted, or active-but-stale → interrupted.
    return "interrupted";
  }

  // No attempt row. A persisted plan (legacy plan-only project, or a lost
  // attempt write) OR a stored first-build prompt (build abandoned pre-plan,
  // before any attempt row existed) with no files is still a stranded first
  // build — offer to continue. Only a project with none of these — no attempt,
  // no plan, no prompt — is genuinely brand-new and owned by the normal build
  // path, so nothing to recover.
  if (hasPlan || hasPrompt) return "interrupted";
  return "none";
}

/**
 * Does the SERVER's record own this project's first-build resume? Pure.
 *
 * One question, asked in one place, because the answer decides which of two
 * things may start the build — and for two releases running, both of them did.
 *
 * The card-first wall sends the buyer to checkout with `returnTo` set to THIS
 * url, `?prompt=` and all, so the ordinary return from a payment lands on a page
 * whose address bar still holds the FIRST thing the user typed. That URL prompt
 * is a resume authority in its own right: the initial-prompt effect re-enters
 * the first-build flow from the top with it. Beside it sits the parked record,
 * which holds the resolved prompt, the setup answers, the acknowledgement and
 * the turn's own source decision, and which resumes itself the moment the
 * entitlement lands. Two owners, one payment, and the race decides which defect
 * the user gets:
 *
 *   • the record wins → the build runs from the saved intent, and the URL replay
 *     lands a moment later on a busy build and is parked in the follow-up queue.
 *     One payment, one build, and the same request sitting queued behind it
 *     (PANDA-2404).
 *   • the URL wins → the build restarts from the raw first message, so the setup
 *     question the user already answered is asked again, and no Continue-build
 *     action is offered because the recovery card is suppressed whenever a URL
 *     prompt is present (PANDA-2414).
 *
 * Neither is fixable by making the race come out the other way; both are fixed
 * by there being one owner. When this returns true the record owns the resume,
 * the URL prompt is consumed without running, and the build starts exactly once
 * — through the claim, after the entitlement has been confirmed.
 *
 * Two shapes count as parked, because migrations are applied by hand in this
 * repo and the status half of 186 can be absent from a live database:
 *   • `awaiting_entitlement` — the real thing.
 *   • `prepared` + `interrupted` — what the park DEGRADES to when the widened
 *     status check is missing (see the 23514 fallback in the build-attempt
 *     route). The row still holds the whole prepared intent, so the URL replay
 *     would double-dispatch and re-ask exactly as above.
 * A `prepared` row that is still `active` is a first build running normally in
 * this very tab and is deliberately NOT parked — nothing has stopped it.
 */
export function preparedBuildOwnsResume(
  attempt: Pick<BuildAttempt, "status" | "stage"> | null | undefined,
): boolean {
  if (!attempt) return false;
  if (attempt.status === "awaiting_entitlement") return true;
  return attempt.stage === "prepared" && attempt.status === "interrupted";
}

/** True when an `active` attempt's heartbeat is older than the stale window. */
export function isAttemptStale(
  attempt: Pick<BuildAttempt, "updatedAt">,
  nowMs: number,
): boolean {
  const t = new Date(attempt.updatedAt).getTime();
  if (Number.isNaN(t)) return true; // unparseable timestamp → treat as stale
  return nowMs - t > BUILD_STALE_MS;
}

// ── Row shape + mapper (used by the API client in lib/api.ts) ─────────────────

/** The build_attempts row as returned by the API route (snake_case). */
export interface BuildAttemptRow {
  id: string;
  project_id: string;
  prompt: string | null;
  plan: ProjectPlan | null;
  attachments: Attachment[] | null;
  ownership_context: Record<string, unknown> | null;
  template_id: string | null;
  /** Absent entirely when migration 186 has not been applied to this DB. */
  request?: PreparedBuildRequest | null;
  stage: BuildStage;
  status: BuildStatus;
  started_at: string;
  updated_at: string;
  /** Added by the GET route from the ledger; not a column. */
  spend?: AttemptSpend | null;
}

/** Map a persisted row to the client-facing BuildAttempt. */
export function fromBuildAttemptRow(row: BuildAttemptRow): BuildAttempt {
  return {
    id: row.id,
    projectId: row.project_id,
    prompt: row.prompt,
    plan: row.plan,
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
    ownershipContext: row.ownership_context,
    templateId: row.template_id,
    request: row.request ?? null,
    stage: row.stage,
    status: row.status,
    startedAt: row.started_at,
    updatedAt: row.updated_at,
    spend: readAttemptSpend(row.spend),
  };
}

function readAttemptSpend(value: unknown): AttemptSpend | null {
  if (!value || typeof value !== "object") return null;
  const state = (value as { state?: unknown }).state;
  return state === "none" || state === "charged" || state === "unknown"
    ? { state }
    : null;
}

/**
 * Settle an attempt's spend from its credit tasks and their ledger rows. Pure,
 * so the route stays a query and the rule is testable on its own.
 *
 * A task that is reserved, running or completed is a charge — the same three
 * statuses the generate route's one-charge-per-attempt guard counts, so
 * "charged" here and "a resume skips the reservation" there are one fact. A
 * task that failed or was refunded counts as nothing only when its ledger rows
 * net to zero; anything else is reported as unknown rather than guessed.
 */
export function summarizeAttemptSpend(
  tasks: readonly { id: string; status: string }[],
  ledger: readonly { task_id: string | null; amount: number }[],
): AttemptSpend {
  if (tasks.length === 0) return { state: "none" };
  if (tasks.some((t) => ["reserved", "running", "completed"].includes(t.status))) {
    return { state: "charged" };
  }
  const ids = new Set(tasks.map((t) => t.id));
  const net = ledger
    .filter((row) => row.task_id !== null && ids.has(row.task_id))
    .reduce((sum, row) => sum + (Number.isFinite(row.amount) ? row.amount : 0), 0);
  return net === 0 ? { state: "none" } : { state: "unknown" };
}

export interface UpsertBuildAttemptInput {
  buildAttemptId: string;
  prompt?: string;
  plan?: ProjectPlan | null;
  attachments?: Attachment[];
  ownershipContext?: Record<string, unknown> | null;
  templateId?: string | null;
  request?: PreparedBuildRequest;
  stage?: BuildStage;
  status?: BuildStatus;
}

// ─────────────────────────────────────────────────────────────────────────
// THE STASHED PROMPT, AFTER THE BUYER HAS PAID (PANDA-2440).
//
// A send refused at the credit gate stashes the prompt in sessionStorage so it
// survives the trip to checkout. That was written for the case where the buyer
// comes back and has to press Send again — and it is still right for that case.
//
// It is wrong for the case the card-first wall created. There, the payment ALSO
// wakes a prepared build_attempts row that resumes the very same request by
// itself, so the buyer returns to a build already running AND to their prompt
// back in the composer under "We saved your last prompt — tap Send to continue
// your build." Two different surfaces describing one request, one of them
// telling them to submit work that is already in progress. Pressing Send there
// is a second build and a second charge.
//
// The fix is not to delete the recovery — a genuinely unstarted request still
// needs it — but to ask first whether anything is already executing this
// request. That is a question about lifecycle state, which is why it is
// answered here, beside preparedBuildOwnsResume, and not inside a 40k-line
// component where it would be a fourth opinion about who owns a resume.
// ─────────────────────────────────────────────────────────────────────────

/**
 * How long a stashed prompt stays restorable. Past this it is a prompt from a
 * different sitting, and putting it back would be a surprise rather than a
 * recovery.
 */
export const PENDING_PROMPT_TTL_MS = 30 * 60_000;

/**
 * What to do with a stashed prompt on load.
 *
 *   • "resume"  — send it now. The email-verification gate reloaded the page
 *                 mid-turn and the user has just cleared it; finishing the turn
 *                 they already submitted is the whole point of that flow.
 *   • "restore" — put it in the composer and say so. Nothing is running and
 *                 nothing will start on its own, so the user decides.
 *   • "drop"    — consume it silently. Either it is spent, or something else is
 *                 already executing it.
 */
export type PendingPromptAction = "resume" | "restore" | "drop";

/** Two stored strings naming the same request. Identity, not interpretation. */
function sameRequest(a: string, b: string | null | undefined): boolean {
  if (!b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function decidePendingPrompt(args: {
  /** The prompt read out of the stash. */
  savedPrompt: string;
  /** When it was stashed (epoch ms). */
  savedAt: number;
  nowMs: number;
  /** The verification gate reloaded us and the user has just verified. */
  justVerified: boolean;
  /** The `?prompt=` this mount was opened with, if any. */
  urlPrompt?: string | null;
  /** What the load path decided about this project's first build. */
  recovery?: {
    state: BuildRecoveryState;
    attempt: Pick<BuildAttempt, "prompt" | "request"> | null;
  } | null;
  /** What the user has typed into the composer since. */
  composerDraft?: string;
}): PendingPromptAction {
  const prompt = args.savedPrompt.trim();
  if (!prompt) return "drop";
  if (args.nowMs - args.savedAt > PENDING_PROMPT_TTL_MS) return "drop";

  // ── A DRAFT IN THE COMPOSER IS THE NEWER INTENT ──────────────────────
  // Overwriting it would take away something the user typed in order to hand
  // back something they typed before paying. The stash is spent either way; the
  // draft is what survives.
  if ((args.composerDraft ?? "").trim()) return "drop";

  // ── PARKED ON A BILLING FACT: THE RECORD RESUMES ITSELF ──────────────
  // awaiting_entitlement is the one state whose next action is "nothing — I'll
  // do it", and the recovery card on screen says exactly that. A composer full
  // of the same request, captioned "tap Send", contradicts it.
  if (args.recovery?.state === "awaiting_entitlement") return "drop";

  // ── SOMETHING IS ALREADY RUNNING THIS EXACT REQUEST ──────────────────
  // The URL prompt is the other resume authority (see preparedBuildOwnsResume):
  // when this mount opened with it, the build either started from it or the
  // prepared record consumed it. Either way the request is owned, and the stash
  // is a copy of it left behind by the gate that stopped it the first time.
  if (sameRequest(prompt, args.urlPrompt)) return "drop";
  const attempt = args.recovery?.attempt;
  if (attempt) {
    if (sameRequest(prompt, attempt.prompt)) return "drop";
    if ((attempt.request?.userTurns ?? []).some((t) => sameRequest(prompt, t)))
      return "drop";
  }

  return args.justVerified ? "resume" : "restore";
}
